import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { FloorPlanFixture, FixtureType } from '@/types/fixture';
import { toast } from 'sonner';

interface UseFloorPlanFixturesOptions {
  establishmentId?: string;
  zoneId?: string | null;
}

interface DbFixture {
  id: string;
  establishment_id: string;
  zone_id: string | null;
  name: string | null;
  type: string;
  x: number;
  y: number;
  width: number;
  height: number;
  color: string | null;
  rotation: number | null;
  created_at: string;
  updated_at: string;
}

export function useFloorPlanFixtures({ establishmentId, zoneId }: UseFloorPlanFixturesOptions) {
  const [fixtures, setFixtures] = useState<FloorPlanFixture[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Fetch fixtures from database
  const fetchFixtures = useCallback(async () => {
    if (!establishmentId) {
      setFixtures([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      let query = supabase
        .from('floor_plan_fixtures')
        .select('*')
        .eq('establishment_id', establishmentId);
      
      if (zoneId) {
        query = query.eq('zone_id', zoneId);
      }

      const { data, error } = await query;

      if (error) throw error;

      const mappedFixtures: FloorPlanFixture[] = (data as DbFixture[] || []).map(f => ({
        id: f.id,
        establishmentId: f.establishment_id,
        zoneId: f.zone_id,
        name: f.name,
        type: f.type as FixtureType,
        x: f.x,
        y: f.y,
        width: f.width,
        height: f.height,
        color: f.color || '#6b7280',
        rotation: f.rotation || 0,
        createdAt: f.created_at,
        updatedAt: f.updated_at,
      }));

      setFixtures(mappedFixtures);
    } catch (error) {
      console.error('Error fetching fixtures:', error);
      toast.error('Erreur lors du chargement des éléments fixes');
    } finally {
      setLoading(false);
    }
  }, [establishmentId, zoneId]);

  // Create a new fixture
  const createFixture = useCallback(async (fixture: Omit<FloorPlanFixture, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (!establishmentId) {
      console.error('createFixture: No establishmentId provided');
      return null;
    }

    try {
      setSaving(true);
      
      const insertData = {
        establishment_id: fixture.establishmentId,
        zone_id: fixture.zoneId,
        name: fixture.name,
        type: fixture.type,
        x: fixture.x,
        y: fixture.y,
        width: fixture.width,
        height: fixture.height,
        color: fixture.color,
        rotation: fixture.rotation,
      };
      
      console.log('Creating fixture with data:', insertData);
      
      const { data, error } = await supabase
        .from('floor_plan_fixtures')
        .insert(insertData)
        .select()
        .single();

      if (error) {
        console.error('Supabase error creating fixture:', error);
        throw error;
      }

      if (!data) {
        console.error('No data returned from fixture creation');
        throw new Error('No data returned');
      }

      console.log('Fixture created successfully:', data);

      const dbFixture = data as DbFixture;
      const newFixture: FloorPlanFixture = {
        id: dbFixture.id,
        establishmentId: dbFixture.establishment_id,
        zoneId: dbFixture.zone_id,
        name: dbFixture.name,
        type: dbFixture.type as FixtureType,
        x: dbFixture.x,
        y: dbFixture.y,
        width: dbFixture.width,
        height: dbFixture.height,
        color: dbFixture.color || '#6b7280',
        rotation: dbFixture.rotation || 0,
        createdAt: dbFixture.created_at,
        updatedAt: dbFixture.updated_at,
      };

      setFixtures(prev => [...prev, newFixture]);
      toast.success('Élément ajouté');
      return newFixture;
    } catch (error) {
      console.error('Error creating fixture:', error);
      toast.error('Erreur lors de la création de l\'élément');
      return null;
    } finally {
      setSaving(false);
    }
  }, [establishmentId]);

  // Update a fixture
  const updateFixture = useCallback(async (id: string, updates: Partial<FloorPlanFixture>) => {
    try {
      setSaving(true);
      
      const dbUpdates: Record<string, unknown> = {};
      if (updates.name !== undefined) dbUpdates.name = updates.name;
      if (updates.type !== undefined) dbUpdates.type = updates.type;
      if (updates.x !== undefined) dbUpdates.x = updates.x;
      if (updates.y !== undefined) dbUpdates.y = updates.y;
      if (updates.width !== undefined) dbUpdates.width = updates.width;
      if (updates.height !== undefined) dbUpdates.height = updates.height;
      if (updates.color !== undefined) dbUpdates.color = updates.color;
      if (updates.rotation !== undefined) dbUpdates.rotation = updates.rotation;
      if (updates.zoneId !== undefined) dbUpdates.zone_id = updates.zoneId;

      const { error } = await supabase
        .from('floor_plan_fixtures')
        .update(dbUpdates)
        .eq('id', id);

      if (error) throw error;

      setFixtures(prev => prev.map(f => 
        f.id === id ? { ...f, ...updates } : f
      ));
      
      return true;
    } catch (error) {
      console.error('Error updating fixture:', error);
      toast.error('Erreur lors de la mise à jour de l\'élément');
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  // Update fixture locally (for dragging)
  const updateFixtureLocal = useCallback((id: string, updates: Partial<FloorPlanFixture>) => {
    setFixtures(prev => prev.map(f => 
      f.id === id ? { ...f, ...updates } : f
    ));
  }, []);

  // Save all local changes
  const saveFixtures = useCallback(async () => {
    try {
      setSaving(true);
      
      const updates = fixtures.map(f => ({
        id: f.id,
        establishment_id: f.establishmentId,
        zone_id: f.zoneId,
        name: f.name,
        type: f.type,
        x: f.x,
        y: f.y,
        width: f.width,
        height: f.height,
        color: f.color,
        rotation: f.rotation,
      }));

      for (const update of updates) {
        const { error } = await supabase
          .from('floor_plan_fixtures')
          .upsert(update);
        
        if (error) throw error;
      }

      toast.success('Éléments sauvegardés');
      return true;
    } catch (error) {
      console.error('Error saving fixtures:', error);
      toast.error('Erreur lors de la sauvegarde des éléments');
      return false;
    } finally {
      setSaving(false);
    }
  }, [fixtures]);

  // Delete a fixture
  const deleteFixture = useCallback(async (id: string) => {
    try {
      setSaving(true);
      
      const { error } = await supabase
        .from('floor_plan_fixtures')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setFixtures(prev => prev.filter(f => f.id !== id));
      toast.success('Élément supprimé');
      return true;
    } catch (error) {
      console.error('Error deleting fixture:', error);
      toast.error('Erreur lors de la suppression de l\'élément');
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  // Load fixtures on mount
  useEffect(() => {
    fetchFixtures();
  }, [fetchFixtures]);

  return {
    fixtures,
    setFixtures,
    loading,
    saving,
    createFixture,
    updateFixture,
    updateFixtureLocal,
    saveFixtures,
    deleteFixture,
    refetch: fetchFixtures,
  };
}
